import React, { useContext, useEffect, useState } from 'react';
import AdminContext from '../../../Context/Admin/adminContext';
import axios from 'axios';
import { getBackendUrl } from '../../../config/apiConfig';
import {
  assignSubAreasToUser,
  fetchSubAreasByDistrict,
  replaceSubAreasForUser,
} from '../../../Services/adminApiService';
import './UserManagement.css';
import { UserForm, UserTable, UserFormsModal, UserSearch } from './components';

const PERMISSION_LABELS = {
  DM: 'District Manager',
  Office: 'Office',
  Admin: 'Administrator',
  AS: 'Area Supervisor',
};

const UserManagement = () => {
  const adminContext = useContext(AdminContext);
  const {
    portalUserList,
    getAllUsers,
    deletePortalUser,
    assignAuthorities,
    replaceAuthorities,
  } = adminContext;
  const backendUrl = getBackendUrl();

  const [searchUsername, setSearchUsername] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showUserForms, setShowUserForms] = useState(false);
  const [userForms, setUserForms] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [authoritiesSelection, setAuthoritiesSelection] = useState([]);
  const [selectedSubAreaIds, setSelectedSubAreaIds] = useState([]);

  const [formData, setFormData] = useState({
    id: null,
    userName: '',
    password: '',
    userFirst: '',
    userLast: '',
    email: '',
    permission: 'DM',
    // For creates we send an array of district objects
    district: [],
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (!portalUserList) {
        setIsLoading(true);
        try {
          await getAllUsers();
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchUsers();
  }, [getAllUsers, portalUserList]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/v1/admin/districts`
        );
        if (Array.isArray(response.data)) {
          setDistricts(response.data);
        }
      } catch (err) {
        console.error('Error loading districts:', err);
      }
    };

    fetchDistricts();
  }, [backendUrl]);

  useEffect(() => {
    const selectedDistrictIds = Array.isArray(formData.district)
      ? formData.district.map((district) => district.id).filter(Boolean)
      : [];

    const shouldLoadSubAreas =
      formData.permission === 'AS' || authoritiesSelection.includes('AS');

    if (!shouldLoadSubAreas || selectedDistrictIds.length === 0) {
      setSubAreas([]);
      setSelectedSubAreaIds([]);
      return;
    }

    let isActive = true;

    const fetchAvailableSubAreas = async () => {
      try {
        const results = await Promise.all(
          selectedDistrictIds.map((districtId) =>
            fetchSubAreasByDistrict(districtId).catch(() => [])
          )
        );
        const merged = results.flat().filter(Boolean);
        const uniqueSubAreas = Array.from(
          new Map(
            merged.map((subArea) => [String(subArea.id), subArea])
          ).values()
        );

        if (!isActive) {
          return;
        }

        setSubAreas(uniqueSubAreas);
        setSelectedSubAreaIds((prev) =>
          prev.filter((subAreaId) =>
            uniqueSubAreas.some(
              (subArea) => String(subArea.id) === String(subAreaId)
            )
          )
        );
      } catch (error) {
        console.error('Error loading sub-areas:', error);
        if (isActive) {
          setSubAreas([]);
        }
      }
    };

    fetchAvailableSubAreas();

    return () => {
      isActive = false;
    };
  }, [formData.district, formData.permission, authoritiesSelection]);

  const loadUser = async (userName) => {
    if (!userName || userName.trim() === '') {
      alert('Please enter a username to search');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Loading user:', userName);
      const response = await axios.get(
        `${backendUrl}/api/v1/admin/getPortalUser`,
        {
          params: { userName: userName.trim() },
        }
      );

      console.log('User data received:', response.data);
      setSelectedUser(response.data);

      // Extract permission from authorities array
      const permission =
        response.data.authorities?.[0]?.authority ||
        response.data.permission ||
        'DM';

      const userData = {
        id: response.data.id,
        userName: response.data.userName || response.data.username,
        userFirst: response.data.userFirst,
        userLast: response.data.userLast,
        email: response.data.email,
        permission: permission,
        // Preserve full district array from backend when editing
        district: Array.isArray(response.data.district)
          ? response.data.district
          : response.data.district
          ? [response.data.district]
          : [],
        password: '',
      };

      console.log('Setting form data:', userData);
      setFormData(userData);
      setIsEditing(true);

      // Initialize authorities selection from loaded user
      const currentAuthorities = Array.isArray(response.data.authorities)
        ? response.data.authorities.map((a) => a.authority).filter(Boolean)
        : response.data.permission
        ? [response.data.permission]
        : [];
      setAuthoritiesSelection(currentAuthorities);
      setSelectedSubAreaIds(
        Array.isArray(response.data.assignedSubAreas)
          ? response.data.assignedSubAreas.map((subArea) => String(subArea.id))
          : []
      );

      // Scroll to form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error loading user:', err);
      alert(
        `Failed to load user: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const createUser = async () => {
    if (!formData.password) {
      alert('Password is required for new users');
      return;
    }

    try {
      const response = await axios.post(`${backendUrl}/api/v1/auth/register`, {
        ...formData,
        permission: formData.permission,
      });
      if (formData.permission === 'AS' && response.data?.id) {
        await assignAuthorities(response.data.id, ['AS']);
      }
      await syncUserSubAreas(response.data?.id, [formData.permission]);
      alert(`User created successfully: ${formData.userName}`);
      resetForm();
      getAllUsers();
    } catch (err) {
      alert(
        `Failed to create user: ${err.response?.data?.message || err.message}`
      );
    }
  };

  const updateUser = async () => {
    try {
      // Prepare payload - remove password if empty (keep existing password)
      const updatePayload = { ...formData };
      if (!updatePayload.password || updatePayload.password.trim() === '') {
        delete updatePayload.password;
      }

      const response = await axios.put(
        `${backendUrl}/api/v1/admin/updatePortalUser`,
        updatePayload
      );
      const effectiveAuthorities = Array.from(
        new Set(
          [...(authoritiesSelection || []), formData.permission].filter(Boolean)
        )
      );
      if (effectiveAuthorities.includes('AS') && response.data?.id) {
        await assignAuthorities(response.data.id, ['AS']);
      }
      await syncUserSubAreas(
        response.data?.id || selectedUser?.id,
        effectiveAuthorities
      );
      alert(`User updated successfully: ${formData.userName}`);
      resetForm();
      getAllUsers();
    } catch (err) {
      alert(
        `Failed to update user: ${err.response?.data?.message || err.message}`
      );
    }
  };

  const loadUserForms = async (userId) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/v1/admin/forms/getFormsByPortalUser/${userId}`
      );
      setUserForms(response.data);
      setShowUserForms(true);
    } catch (err) {
      alert(
        `Failed to load user forms: ${
          err.response?.data?.message || err.message
        }`
      );
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      userName: '',
      password: '',
      userFirst: '',
      userLast: '',
      email: '',
      permission: 'DM',
      district: [],
    });
    setSelectedUser(null);
    setIsEditing(false);
    setAuthoritiesSelection([]);
    setSelectedSubAreaIds([]);
    setSubAreas([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      updateUser();
    } else {
      createUser();
    }
  };

  const toggleAuthority = (role) => {
    setAuthoritiesSelection((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleSubAreaSelectionChange = (e) => {
    const values = Array.from(e.target.selectedOptions).map(
      (option) => option.value
    );
    setSelectedSubAreaIds(values);
  };

  const syncUserSubAreas = async (userId, authorities) => {
    if (!userId) {
      return;
    }

    const normalizedAuthorities = Array.isArray(authorities)
      ? Array.from(new Set(authorities.filter(Boolean)))
      : [];

    if (!normalizedAuthorities.includes('AS')) {
      return;
    }

    const subAreaIds = selectedSubAreaIds.map((subAreaId) => Number(subAreaId));
    await assignSubAreasToUser(userId, subAreaIds);
    await replaceSubAreasForUser(userId, subAreaIds);
  };

  const handleAssignAuthorities = async () => {
    if (!selectedUser?.id) {
      alert('Load a user before assigning authorities.');
      return;
    }
    if (!authoritiesSelection.length) {
      alert('Select at least one authority.');
      return;
    }
    const result = await assignAuthorities(
      selectedUser.id,
      authoritiesSelection
    );
    if (result.success) {
      alert('Authorities assigned successfully');
      await loadUser(selectedUser.userName);
    } else {
      alert(`Failed to assign authorities: ${result.error}`);
    }
  };

  const handleReplaceAuthorities = async () => {
    if (!selectedUser?.id) {
      alert('Load a user before replacing authorities.');
      return;
    }
    if (!authoritiesSelection.length) {
      alert('Select at least one authority.');
      return;
    }
    const result = await replaceAuthorities(
      selectedUser.id,
      authoritiesSelection
    );
    if (result.success) {
      alert('Authorities replaced successfully');
      await loadUser(selectedUser.userName);
    } else {
      alert(`Failed to replace authorities: ${result.error}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, selectedOptions, multiple } = e.target;

    // Special handling for district multi-select: store array of full district objects
    if (name === 'district') {
      if (!multiple) {
        // Fallback for non-multiple usage
        if (!value) {
          setFormData((prev) => ({ ...prev, district: [] }));
          return;
        }
        const selected = districts.find(
          (d) => d.districtName === value || d.id?.toString() === value
        );
        setFormData((prev) => ({
          ...prev,
          district: selected ? [selected] : [],
        }));
        return;
      }

      const values = Array.from(selectedOptions).map((o) => o.value);
      const selectedDistricts = districts.filter(
        (d) => values.includes(d.districtName) || values.includes(String(d.id))
      );
      setFormData((prev) => ({
        ...prev,
        district: selectedDistricts,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredUsers =
    portalUserList?.filter((user) => {
      const searchLower = filterText.toLowerCase();
      return (
        user.userName?.toLowerCase().includes(searchLower) ||
        user.userFirst?.toLowerCase().includes(searchLower) ||
        user.userLast?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    }) || [];

  const handleDeleteUser = async (userId, userName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete user "${userName}"? This will disable the account.`
      )
    ) {
      return;
    }

    const result = await deletePortalUser(userId);

    if (result.success) {
      alert('User deleted successfully');
      // Clear form if the deleted user was being edited
      if (selectedUser?.id === userId) {
        resetForm();
      }
    } else {
      alert(`Failed to delete user: ${result.error}`);
    }
  };

  return (
    <div className="user-management">
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      )}
      <div className="management-header">
        <h1>User Management</h1>
        <p className="management-subtitle">
          Manage portal users and permissions
        </p>
      </div>

      {/* Search User Section */}
      <UserSearch
        searchUsername={searchUsername}
        onSearchChange={setSearchUsername}
        onSearch={() => loadUser(searchUsername)}
      />

      {/* User Form Section */}
      <UserForm
        formData={formData}
        isEditing={isEditing}
        selectedUser={selectedUser}
        districts={districts}
        authoritiesSelection={authoritiesSelection}
        permissions={PERMISSION_LABELS}
        subAreas={subAreas}
        selectedSubAreaIds={selectedSubAreaIds}
        showSubAreaSelection={
          formData.permission === 'AS' || authoritiesSelection.includes('AS')
        }
        onSubAreaSelectionChange={handleSubAreaSelectionChange}
        onInputChange={handleInputChange}
        onSubmit={handleSubmit}
        onCancel={resetForm}
        onToggleAuthority={toggleAuthority}
        onAssignAuthorities={handleAssignAuthorities}
        onReplaceAuthorities={handleReplaceAuthorities}
      />

      {/* User List Section */}
      <UserTable
        users={filteredUsers}
        filterText={filterText}
        onFilterChange={setFilterText}
        onEdit={loadUser}
        onViewForms={loadUserForms}
        onDelete={handleDeleteUser}
      />

      {/* User Forms Modal */}
      <UserFormsModal
        isOpen={showUserForms}
        userForms={userForms}
        onClose={() => setShowUserForms(false)}
      />
    </div>
  );
};

export default UserManagement;
